import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import modelsData from '../data/models.json';

const SentimentAnalysis = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const language = searchParams.get('language') || 'tn';
  
  // Language configuration
  const languageConfig = {
    tn: {
      title: 'Sentiment Analysis',
      description: 'Analyze sentiment in Tunisian Arabic text using our state-of-the-art NLP model.',
      examples: [
        "يعطيكم الصحة على الخدمة الباهية.",
        "الانترنت ديما طايحة ومفيهاش استقرار.",
        "خدمتكم ممتازة وسريعة، شكراً ليكم!",
        "الفاتورة غالطة ومحد جاوبني!"
      ]
    },
    ma: {
      title: 'Sentiment Analysis',
      description: 'Analyze sentiment in Moroccan Arabic text using our state-of-the-art NLP model.',
      examples: [
        "الله يعطيكم الصحة على الخدمة الزوينة.",
        "الانترنت ديما طايحة ومافيهاش استقرار.",
        "خدمتكم ممتازة وسريعة، شكراً ليكم!",
        "الفاتورة غالطة ومحد جاوبني!"
      ]
    }
  };

  const currentConfig = languageConfig[language] || languageConfig.tn;
  
  const [text, setText] = useState('');
  const [sentiment, setSentiment] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [selectedExample, setSelectedExample] = useState('');

  // Scroll to top when component mounts or language changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [language]);

  const examples = currentConfig.examples;

  // Function to check if text is predominantly Arabic
  const isArabicDominant = (text) => {
    if (!text) return false;
    const arabicChars = text.replace(/[^\u0600-\u06FF]/g, '').length;
    return arabicChars > text.length / 2;
  };

  // Handle example click
  const handleExampleClick = (example) => {
    setText(example);
    setSelectedExample(example);
    setSentiment(null); // Reset sentiment before new analysis
    analyzeText(example);
  };

  // Clear selected example when text changes
  const handleTextChange = (e) => {
    setText(e.target.value);
    if (e.target.value !== selectedExample) {
      setSelectedExample('');
    }
  };

  // Analyze text function
  const analyzeText = async (textToAnalyze) => {
    if (!textToAnalyze.trim()) {
      setSentiment(null);
      return;
    }
    
    setIsAnalyzing(true);
    setSentiment(null); // Reset sentiment before new analysis
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/sentiment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          text: textToAnalyze,
          language: language
        }),
      });
      
      if (!response.ok) throw new Error('API error');
      const data = await response.json();

      setSentiment({ label: data.label });
    } catch (error) {
      console.error('Error analyzing sentiment:', error);
      setSentiment({ label: 'Error' });
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Main analyze function
  const handleAnalyze = () => {
    analyzeText(text);
  };

  const getSentimentPosition = () => {
    if (!sentiment) return '50%';
    if (sentiment.label === 'Positive') return '100%';
    if (sentiment.label === 'Negative') return '0%';
    return '50%';
  };

  const getIndicatorColor = () => {
    if (!sentiment) return '#999';
    if (sentiment.label === 'Positive') return 'green';
    if (sentiment.label === 'Negative') return 'red';
    return 'gray';
  };

  const getEmotionIcon = () => {
    if (!sentiment) return '😐';
    if (sentiment.label === 'Positive') return '😀';
    if (sentiment.label === 'Negative') return '😔';
    if (sentiment.label === 'Error') return '❌';
    return '😐';
  };

  // Auto-select an example on component mount
  useEffect(() => {
    // Select the positive example (index 2)
    const exampleToSelect = examples[2]; // "خدمتكم ممتازة وسريعة، شكراً ليكم!"
    setText(exampleToSelect);
    setSelectedExample(exampleToSelect);
    // Don't auto-analyze here, let the user click "Analyze" or the example
  }, []);

  useEffect(() => {
    if (!text.trim()) {
      setSentiment(null);
    }
  }, [text]);

  return (
    <div className="demo-page-container">
      <div className="demo-page-content sentiment-container">
        {/* Side-by-side Grid Layout */}
        <div className="sentiment-grid">
          {/* Left Column - Input */}
          <div className="sentiment-column">
            <h3 className="sentiment-column-title">Text to analyze</h3>
            <textarea
              placeholder="Enter text to analyze sentiment..."
              rows={5}
              value={text}
              onChange={handleTextChange}
              className={`sentiment-textarea ${isArabicDominant(text) ? 'rtl' : ''}`}
              style={{ flex: 1, minHeight: '120px' }}
            />

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                className="analyze-button"
                onClick={handleAnalyze}
                disabled={!text.trim() || isAnalyzing}
                style={{
                  opacity: !text.trim() || isAnalyzing ? 0.7 : 1
                }}
              >
                {isAnalyzing ? 'Analyzing...' : 'Analyze'}
              </button>
            </div>

            {/* Examples Section */}
            <div style={{ marginTop: '20px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: '500', color: '#6B7280', marginBottom: '12px' }}>
                Need inspiration? Try one of these:
              </h4>
              <div className="examples-grid" style={{ gap: '8px' }}>
                {examples.map((example, index) => (
                  <button
                    key={index}
                    className={`example-button ${isArabicDominant(example) ? 'rtl' : ''} ${example === selectedExample ? 'selected' : ''}`}
                    onClick={() => handleExampleClick(example)}
                    style={{ 
                      padding: '8px 12px', 
                      fontSize: '13px',
                      minHeight: '40px'
                    }}
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Result */}
          <div className="sentiment-column">
            <h3 className="sentiment-column-title">Result</h3>
            
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
              {sentiment ? (
                <div className="sentiment-result" style={{ 
                  width: '100%', 
                  padding: '16px',
                  display: 'flex', 
                  flexDirection: 'column', 
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <div className="sentiment-emoji" style={{ fontSize: '36px', marginBottom: '8px' }}>
                    {getEmotionIcon()}
                  </div>
                  <h3 className="sentiment-result-title" style={{ fontSize: '16px', marginTop: '0', marginBottom: '8px' }}>
                    {sentiment.label === 'Positive' ? 'Positive Sentiment' : 
                     sentiment.label === 'Negative' ? 'Negative Sentiment' : 
                     sentiment.label === 'Error' ? 'Analysis Error' : 'Neutral Sentiment'}
                  </h3>
                  
                  {sentiment.label !== 'Error' && (
                    <div className="sentiment-gauge-container" style={{ marginTop: '8px', width: '100%' }}>
                      <div className="sentiment-gauge-track" style={{ height: '6px' }}>
                        <div 
                          className="sentiment-gauge-indicator" 
                          style={{ 
                            background: getIndicatorColor(),
                            left: getSentimentPosition(),
                            width: '16px',
                            height: '16px'
                          }} 
                        />
                      </div>
                      
                      <div className="sentiment-gauge-labels" style={{ fontSize: '11px', marginTop: '4px' }}>
                        <span>Negative</span>
                        <span>Neutral</span>
                        <span>Positive</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ textAlign: 'center', color: '#777' }}>
                  <span className="material-icons" style={{ fontSize: 48, color: '#ddd', marginBottom: '16px' }}>sentiment_satisfied</span>
                  <p>Sentiment analysis results will appear here</p>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Visual separator */}
        <div style={{ 
          width: '100%', 
          height: '1px', 
          background: '#eee', 
          margin: '20px auto 10px auto',
          maxWidth: '80%'
        }}></div>
      </div>
    </div>
  );
};

export default SentimentAnalysis;